import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { SectionHeading } from '@/components/base/SectionHeading';
import { brandInfo } from '@/mocks/brand';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [activeTab, setActiveTab] = useState<'story' | 'reputation' | 'contact'>('story');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u1oflca23r0tr1aov0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = { code: '' };
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于幼水若若',
            description: brandInfo.description,
            url: 'https://ruoruworld.github.io/about',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ruoruworld.github.io/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '幼水若若',
            url: 'https://ruoruworld.github.io/',
            description: brandInfo.description,
            foundingDate: '2023',
            address: {
              '@type': 'PostalAddress',
              addressLocality: '杭州',
              addressCountry: 'CN',
            },
            contactPoint: {
              '@type': 'ContactPoint',
              email: brandInfo.contact.email,
              telephone: brandInfo.contact.phone,
              contactType: 'customer service',
            },
          }),
        }}
      />

      {/* Hero */}
      <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20tranquil%20tea%20plantation%20at%20sunrise%20in%20Yunnan%20China%2C%20morning%20dew%20on%20tea%20leaves%2C%20soft%20mist%20rolling%20over%20the%20hills%2C%20warm%20golden%20light%20breaking%20through%20clouds%2C%20serene%20and%20inspirational%20landscape%20with%20warm%20earthy%20tones&width=1600&height=900&seq=about-hero&orientation=landscape"
            alt="幼水若若品牌起源"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/35"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-6">
          <span className="text-xs tracking-[0.3em] text-white/70 uppercase font-accent">
            About Us
          </span>
          <h1 className="mt-3 text-3xl md:text-5xl font-heading text-white tracking-widest">
            关于我们
          </h1>
          <p className="mt-4 text-sm text-white/70 max-w-md text-center leading-relaxed">
            {brandInfo.tagline}
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="sticky top-16 z-30 bg-background-50 border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-1 py-1">
            {[
              { key: 'story' as const, label: '品牌故事' },
              { key: 'reputation' as const, label: '用户口碑' },
              { key: 'contact' as const, label: '联系我们' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="py-4">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          </div>
        </div>
      </div>

      {/* Brand Story */}
      {activeTab === 'story' && (
        <section className="pb-20 md:pb-28 px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">品牌故事</h2>
              <p className="mt-4 text-sm text-foreground-500 leading-relaxed">
                {brandInfo.description}
              </p>
            </div>

            {/* Philosophy */}
            <div className="mb-12">
              <h3 className="text-lg font-heading text-foreground-800">品牌理念</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {brandInfo.philosophy.items.map((item, index) => (
                  <div key={index} className="p-6 rounded-lg bg-background-100 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent-100/80 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-accent-600`}></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-700">{item.title}</h4>
                      <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Milestones */}
            <div>
              <h3 className="text-lg font-heading text-foreground-800">品牌历程</h3>
              <div className="mt-6 relative">
                <div className="absolute left-3.5 top-0 bottom-0 w-px bg-background-300"></div>
                <div className="flex flex-col gap-6">
                  {brandInfo.milestones.map((milestone, index) => (
                    <div key={index} className="flex items-start gap-5 relative">
                      <div className="w-7 h-7 rounded-full bg-accent-100 border-2 border-accent-300 flex items-center justify-center flex-shrink-0 relative z-10">
                        <div className="w-2 h-2 rounded-full bg-accent-500"></div>
                      </div>
                      <div className="pt-0.5">
                        <span className="text-xs font-medium text-accent-600">{milestone.year}</span>
                        <p className="mt-0.5 text-sm text-foreground-600">{milestone.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Reputation */}
      {activeTab === 'reputation' && (
        <section className="pb-20 md:pb-28 px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">用户口碑</h2>
              <p className="mt-2 text-sm text-foreground-500">
                来自真实用户的反馈，是我们前行的最大动力。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {brandInfo.reputation.map((review) => (
                <div key={review.id} className="p-5 rounded-lg bg-background-100">
                  <div className="flex items-center gap-1 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < review.rating ? 'ri-star-fill' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    "{review.comment}"
                  </p>
                  <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
                    <span>{review.name} · {review.location}</span>
                    <span>{review.date}</span>
                  </div>
                  <span className="mt-2 inline-block px-2.5 py-1 rounded-full bg-secondary-100/80 text-secondary-700 text-xs">
                    {review.product}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      {activeTab === 'contact' && (
        <section className="pb-20 md:pb-28 px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">联系我们</h2>
              <p className="mt-2 text-sm text-foreground-500">
                有任何问题或合作意向，欢迎随时与我们联系。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">邮箱</p>
                      <a href={`mailto:${brandInfo.contact.email}`} className="text-sm text-foreground-700 hover:text-primary-500 transition-colors">
                        {brandInfo.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电话</p>
                      <span className="text-sm text-foreground-700">{brandInfo.contact.phone}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">地址</p>
                      <span className="text-sm text-foreground-700">{brandInfo.contact.address}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <p className="text-xs text-foreground-400 mb-3">关注我们</p>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground-600">微信：{brandInfo.contact.wechat}</span>
                    <span className="text-sm text-foreground-600">微博：{brandInfo.contact.weibo}</span>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="text-xs text-foreground-500">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="mt-1 w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-xs text-foreground-500">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="mt-1 w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="text-xs text-foreground-500">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="mt-1 w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您想了解的内容（限500字）"
                    ></textarea>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    className="spam-protection-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors duration-300 cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">消息已发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}